import { Button, Menu, MenuButton, MenuList, MenuOptionGroup, MenuItemOption } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons/faAngleDown';
import aveta from 'aveta';
import { FC, useCallback, useRef, useState } from 'react';
import lodash from 'lodash';

const { flatten } = lodash;

interface IDropdownOption {
  value: string;
  label: string;
}
interface IGroup {
  options: IDropdownOption[];
  label?: string;
}

export const createLabelGenerator =
  (pageSize: number, onlyPageNumber = false) =>
  (page: number) => {
    if (onlyPageNumber) {
      return `Page ${page}`;
    }

    const prevPage = Math.max(page - 1, 0);
    const currStart = prevPage * pageSize + 1;
    const currEnd = page * pageSize;

    return `${currStart}-${currEnd}`;
  };

export const generateOptions = (
  currentPage: number,
  maxPage: number,
  pageSize: number,
  onlyPageNumber?: boolean
): IGroup[] => {
  const generateLabel = createLabelGenerator(pageSize, onlyPageNumber);
  const maxOffset = 4; // derived from - page 1 + 2 x neighbouringIndex + currentIndex
  const allOptions = [];
  let options = [{ value: `1`, label: generateLabel(1) }];
  if (currentPage > maxOffset) {
    allOptions.push({ options: options });
    options = [];
  }
  if (currentPage > maxOffset - 1) {
    options.push({
      value: `${currentPage - (maxOffset - 2)}`,
      label: generateLabel(currentPage - (maxOffset - 2))
    });
  }
  if (currentPage > maxOffset - 2) {
    options.push({
      value: `${currentPage - (maxOffset - 3)}`,
      label: generateLabel(currentPage - (maxOffset - 3))
    });
  }
  if (currentPage > maxOffset - 3) {
    options.push({ value: `${currentPage}`, label: generateLabel(currentPage) });
  }
  if (currentPage < maxPage - 1) {
    options.push({ value: `${currentPage + 1}`, label: generateLabel(currentPage + 1) });
  }
  if (currentPage < maxPage - 2) {
    options.push({ value: `${currentPage + 2}`, label: generateLabel(currentPage + 2) });
  }
  if (currentPage < maxPage - 3) {
    allOptions.push({ options: options });
    options = [];
  }
  if (currentPage !== maxPage) {
    options.push({ value: `${maxPage}`, label: generateLabel(maxPage) });
  }
  allOptions.push({ options: options });

  return allOptions;
};
export interface IProps {
  displaySelectedPage: (selectedPage: number) => void;
  selectedPage: number;
  maxPage: number;
  pageSize: number;
  disabled?: boolean;
  onlyPageNumber?: boolean;
}

export const PageSelector: FC<IProps> = ({
  onlyPageNumber,
  selectedPage,
  maxPage,
  pageSize,
  displaySelectedPage,
  disabled
}) => {
  const defaultOptionRef = useRef();
  const [isOpen, setIsOpen] = useState(false);

  const onClickHandler = useCallback(
    (value: string) => {
      // console.log(`Clicked ${value}`);
      setIsOpen(false);
      displaySelectedPage(parseInt(value));
    },
    [setIsOpen, displaySelectedPage]
  );
  const onOpenHandler = useCallback(() => setIsOpen(true), [setIsOpen]);
  const onCloseHandler = useCallback(() => setIsOpen(false), [setIsOpen]);
  const allOptions = generateOptions(selectedPage, maxPage, pageSize, onlyPageNumber);

  return (
    <Menu isOpen={isOpen} onOpen={onOpenHandler} onClose={onCloseHandler} initialFocusRef={defaultOptionRef}>
      <MenuButton
        as={Button}
        rightIcon={<FontAwesomeIcon className="utility-icon" icon={faAngleDown} />}
        isDisabled={disabled}
        variant="ghost"
        size="sm"
        flexShrink="0"
      >
        {onlyPageNumber
          ? `Page ${selectedPage}`
          : `${createLabelGenerator(pageSize, onlyPageNumber)(selectedPage)} of ${aveta(maxPage * pageSize, {
              precision: 2
            })}`}
      </MenuButton>

      <MenuList fontSize="sm">
        <MenuOptionGroup value={`${selectedPage}`} type="radio">
          {flatten(
            allOptions.map(({ options }, index) =>
              options.map(({ value, label }) => (
                <MenuItemOption
                  key={`${index}-${value}`}
                  value={value}
                  onClick={() => onClickHandler(value)}
                  ref={parseInt(value) === selectedPage ? defaultOptionRef : null}
                >
                  {label}
                </MenuItemOption>
              ))
            )
          )}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
};
